import _ from "lodash";
import React, { lazy } from "react";
import { Plegma } from "@API/Api";
import { WebNode } from "@API/WebNode";
import { WebThingPortType } from "@API/WebNodeTypes";
import { WebThing, WebThingPort } from "@API/WebThing";
import { LazyLoadingAnimation } from "@Primitive/Lazy";
import { IconURIPath, LazyWidget } from "@UI/Widgets";
import { isDefined } from "@Utils/Micro";
import { IWidgetDescription, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from "../../../Widgets/WidgetStructs";
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { CodeEditorData, CodeEditorKeys, CodeEditorTheme } from "./CodeEditorStructs";
// =================================================================================================
const CodeEditor = lazy(() => import("./CodeEditorAsync"));
const CodeEditorSettings = lazy(() => import("./CodeEditorAsync").then((module) => ({ default: module.CodeEditorSettings, })));
// =================================================================================================
export class CodeEditorWidget extends IWidgetThingsClass<CodeEditorData> {
    Render(): React.ReactNode {
        return (
            <LazyWidget>
                <CodeEditor />
            </LazyWidget>
        );
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <React.Suspense fallback={<LazyLoadingAnimation />}>
            <CodeEditorSettings
                key={'settings-' + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)}
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </React.Suspense>
    }

    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(
            new WebThingPort(
                {
                    Name: "Settings",
                    PortKey: CodeEditorKeys.InputData,
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    State: "",
                },
                WebThingPortType.Data
            )
        );

        ports.push(
            new WebThingPort(
                {
                    Name: "Saved Code",
                    PortKey: CodeEditorKeys.OutputData,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: "",
                },
                WebThingPortType.Trigger
            )
        );

        ports.push(
            new WebThingPort(
                {
                    Name: "ToolbarIcon Pressed",
                    PortKey: CodeEditorKeys.OnToolbarIconClick,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: "",
                },
                WebThingPortType.Trigger
            )
        );

        return ports;
    }

    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        //Add thing's ports
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }

    initThing() {
        // Create plegma thing
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing
        let ports = this.createThingPorts();

        // Create the thing
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = webThing.ThingKey;
    }

    public static GetDefaultWidgetDataStatic(): CodeEditorData {
        return {
            theme: {
                editorTheme: CodeEditorTheme.vsDark,
                iconColor: "white",
                toolbarColor: "#134459",
                toolbarHeight: 30,
                toolbarIconSize: 16
            },
            icons: {
                alignButton: true,
                wrapButton: true,
                saveButton: true,
                discardButton: true,
            },
            customLeftToolbarIcons: [],
            customCenterToolbarIcons: [],
            customRightToolbarIcons: [],

            isStrictSaved: false,
        };
    }

    public GetDefaultWidgetData() {
        return CodeEditorWidget.GetDefaultWidgetDataStatic();
    }

    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget description
        return {
            Title: "Code Editor",
            WidgetTitle: "Code Editor Widget",
            Key: "CodeEditorWidget",
            Type: "CodeEditorWidget",
            Data: CodeEditorWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI:
                IconURIPath + "/svg/code_editor_128.svg"
        };
    }

    public ValidateValues(): IWidgetDataValidateResp {
        // TODO: Set schema validators
        return { valid: true, errors: "" };
    }
}